import { clearSessionToken, readSessionToken, sessionResumeBlocked, writeSessionToken } from "./http";
import { clearIdentityCache, loadIdentity } from "./identity";

const ENDPOINT = "/v1/auth/device-session";
const headers = { "X-Link-Device-Session": "1" };

interface Dependencies {
  fetch: typeof fetch;
  read: () => string | null;
  write: (token: string) => void;
  clear: () => void;
  blocked: () => boolean;
  hasIdentity: (account: string) => Promise<boolean>;
}

const dependencies: Dependencies = {
  fetch: (...args) => fetch(...args),
  read: readSessionToken,
  write: writeSessionToken,
  clear: () => { clearSessionToken(); clearIdentityCache(); },
  blocked: sessionResumeBlocked,
  hasIdentity: async (account) => (await loadIdentity(account)) !== null,
};

function request(deps: Dependencies, method: string, token?: string) {
  return deps.fetch(ENDPOINT, {
    method, credentials: "same-origin", cache: "no-store",
    headers: { ...headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal: AbortSignal.timeout(8_000),
  });
}

/** Save only the session, never a phrase, seed, or private key. */
export async function rememberCurrentSession(deps = dependencies): Promise<void> {
  const token = deps.read();
  if (!token || deps.blocked()) return;
  const response = await request(deps, "PUT", token);
  if (!response.ok) throw new Error("Link couldn't keep this device signed in. Check your connection and try again.");
}

/** Runs before providers mount, so no sign-in flash or unauthenticated data fetches. */
export async function restoreDeviceSession(deps = dependencies): Promise<void> {
  if (deps.blocked()) {
    // Retry remote revocation after an offline sign-out, but never auto-sign in.
    await request(deps, "DELETE").catch(() => undefined);
    return;
  }
  const previous = deps.read();
  const response = await request(deps, previous ? "PUT" : "POST", previous ?? undefined);
  // Logging out or explicitly changing account wins over an in-flight restore.
  if (deps.blocked() || deps.read() !== previous) return;
  if (response.status === 204 || response.status === 401 || response.status === 403) {
    deps.clear();
    return;
  }
  if (!response.ok) throw new Error("Link couldn't reconnect. Your saved sign-in has not been removed.");
  const session = await response.json() as { account_id?: unknown; session_token?: unknown };
  const token = previous ?? session.session_token;
  if (typeof session.account_id !== "string" || !session.account_id ||
      typeof token !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(token)) {
    throw new Error("Link couldn't verify the saved session. Please try again.");
  }
  if (deps.blocked() || deps.read() !== previous) return;
  // The server token is insufficient to decrypt anything. Require BOTH local
  // signing and ECDH keys; never generate replacements or fetch plaintext keys.
  deps.write(token);
  let present: boolean;
  try { present = await deps.hasIdentity(session.account_id); }
  catch {
    if (deps.blocked() || deps.read() !== token) return;
    deps.clear();
    throw new Error("Link couldn't open this device's identity keys. Please sign in again.");
  }
  if (deps.blocked() || deps.read() !== token) return;
  if (!present) {
    deps.clear();
    await request(deps, "DELETE").catch(() => undefined);
  }
}

/** Local sign-out is immediate, even offline; server revocation is attempted too. */
export async function signOutSession(deps = dependencies): Promise<void> {
  const token = deps.read();
  deps.clear();
  await Promise.allSettled([
    request(deps, "DELETE"),
    ...(token ? [deps.fetch("/v1/auth/logout", {
      method: "POST", credentials: "same-origin", cache: "no-store",
      headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(8_000),
    })] : []),
  ]);
}
